import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, InputOnChangeData, Label, Link, Textarea, TextareaOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Consultant, ConsultantDocument } from '../../interfaces/Consultant';
import { ApplicationState } from '../../store';
import * as TeamsState from '../../store/TeamsState';
import { ConsultantActionCreators } from '../../store/consultants';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import FilesDragAndDrop from '../Tools/FilesDragAndDrop/FilesDragAndDrop';
import { TokenHelper } from '../../TokenHelper';
import { ConsultantDropdown } from '../EditorHelpers/ConsultantDropdown';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { UserLoginState } from '../../store/Login';
import { onFormatDate } from '../Tools/converters';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { DocumentTypeDropdown } from '../EditorHelpers/DocumentTypeDropDown';
import { FileViewer } from '../FileViewer/FileViewer';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { ChangeEvent } from 'react';

//function whatDecimalSeparator() {
//    var n = 1.1;
//    return n.toLocaleString().substring(1, 2);
//}

export const ConsultantsDocumentsCreation = () => {
    // All data needed for edition should be loaded before calling the editor
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const consultantDocumentCreate = useSelector<ApplicationState, ConsultantDocument | null>((state: ApplicationState) => state.consultantsState.consultantDocumentCreate);
    const consultants = useSelector<ApplicationState, Consultant[] | undefined>((state: ApplicationState) => state.consultantsState.consultants);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [file, setFile] = React.useState<File>();
    const [documentType, setDocumentType] = React.useState<number | null>(null);
    const [documentSource, setDocumentSource] = React.useState<string | null>(null);
    const [documentDate, setDocumentDate] = React.useState<Date | null>(null);
    const [documentAmount, setDocumentAmount] = React.useState<number | null>(null);
    const [consultant, setConsultant] = React.useState<Consultant | null | undefined>(undefined);
    const [description, setDescription] = React.useState<string | null>(null);
    const [selConsultant, setSelConsultant] = React.useState<boolean>(true);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);

    React.useEffect(() => {
        if (consultants && consultantDocumentCreate && consultantDocumentCreate.consultantid) {
            const c = consultants.find(c => c.consultantid?.toLowerCase() === consultantDocumentCreate.consultantid?.toLowerCase());
            if (c) {
                setConsultant(c);
                setSelConsultant(false);
            }
        }
        if (consultantDocumentCreate && consultantDocumentCreate.documentTypeId !== -1) {
            setDocumentType(consultantDocumentCreate.documentTypeId);
        }
    }, [consultantDocumentCreate, consultants]);

    React.useEffect(() => {
        setSubmitValid(file !== undefined
            && documentType !== null
            && documentSource !== null && documentSource.length > 0
            && documentDate !== null
        );
    }, [file, documentType, documentSource, documentDate, documentAmount, consultant, description]);

    const submit = () => {
        if (file && documentSource && documentDate !== null) {
            const c = new FormData();
            c.append("formFile", file);
            c.append("Consultantid", consultant && consultant.consultantid ? consultant.consultantid : "");
            c.append("DocumentTypeId", documentType ? documentType.toString() : "");
            c.append("DocumentSource", documentSource);
            c.append("DocumentDate", ConvertionHelpers.DateToISODateOnly(documentDate) ?? "");
            if (description) {
                c.append("Description", description);
            }
            if (documentAmount) {
                c.append("DocumentAmount", documentAmount.toString());
            }

            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null) {
                        const toastId = (Math.random() + 1).toString(36).substring(7);
                        dispatchToast(
                            <Toast appearance="inverted">
                                <ToastTitle>
                                    Saving...
                                </ToastTitle>
                            </Toast>,
                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                        );
                        fetch("api/ConsultantsDocuments/NewDocument", {
                            method: "POST",
                            headers: {
                                authorization: "bearer " + token,
                            },
                            body: c,
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Saved
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "success",
                                        toastId,
                                        timeout: 5000,
                                    });
                                    return response.json() as Promise<ConsultantDocument>;
                                }
                                else {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Save failed
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "error",
                                        toastId,
                                        timeout: 10000,
                                    });
                                }
                            })
                            .then(data => {
                                if (data) {
                                    dispatch(ConsultantActionCreators.patchConsultantDocumentEdition(data));
                                    dispatch(ConsultantActionCreators.requestConsultantDocumentCreation(null));
                                }
                            });
                    }
                });
        }
    };

    //console.log("documentAmount", documentAmount);

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={styles.DialogContentWithFilePreview}>
            <DialogBody className={styles.fluid}>
                <DialogTitle>{t('CreateNewDocument', { consultant: consultant ? (consultant.companyName ?? consultant.displayName) : "?", interpolation: { escapeValue: false } })}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        {file === undefined &&
                            <FilesDragAndDrop
                                onUpload={(files) => {
                                    if (files.length === 1) {
                                        setFile(files[0]);
                                    }
                                }}
                                count={1}
                                formats={['jpg', 'png', 'pdf']}
                                containerStyles={{
                                    width: '100%',
                                    height: '100%',
                                    border: '2px #c3c3c3 dashed',
                                    borderRadius: '12px',
                                }}
                                openDialogOnClick
                                successText=""
                                successTime={0}
                            >
                                <div className={styles.ColFullDragDrop}>
                                    <Label className={styles.label}>{t('ClickDropFileHere')}</Label>
                                </div>
                            </FilesDragAndDrop>
                        }
                        {file &&
                            <FileViewer file={file} />
                        }
                        <div className={styles.InfoPart}>
                            <form
                                onSubmit={() => {
                                    dispatch(ConsultantActionCreators.requestConsultantDocumentCreation(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                {(UserLoginState.rigths && UserLoginState.rigths.isAdmin) && selConsultant &&
                                    <Field label={t('Consultant')} className={styles.fluid}>
                                        <ConsultantDropdown
                                            currentConsultant={consultant ?? undefined}
                                            setConsultant={setConsultant}
                                        />
                                    </Field>
                                }
                                <Field label="Type" required>
                                    <DocumentTypeDropdown
                                        currentDocumentType={documentType}
                                        setDocumentType={setDocumentType}
                                    />
                                </Field>
                                <Field label="Source" required>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setDocumentSource(data.value)}
                                        value={documentSource !== null ? documentSource : ""}
                                        aria-invalid={documentSource === null}
                                        maxLength={50}
                                    />
                                </Field>
                                <Field label="Date"
                                    className={styles.fluid}
                                    required
                                >
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setDocumentDate(date === undefined ? null : date); }}
                                        value={documentDate}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectDocumentDate')}
                                        aria-invalid={documentDate === null}
                                        minDate={new Date(2024, 0, 1)}
                                        maxDate={new Date()}
                                    />
                                </Field>
                                <Field label={t('Amount') + " (" + t('IncludVAT') + ")"}
                                    className={styles.fluid}
                                >
                                    <CurrencyInput
                                        setCurrencyOrNull={setDocumentAmount}
                                        currency={documentAmount}
                                        digits={2}
                                    />
                                </Field>
                                <Field label="Description">
                                    <Textarea
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setDescription(data.value)}
                                        value={description !== null ? description : ""}
                                        resize="vertical"
                                    />
                                </Field>
                            </form>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(ConsultantActionCreators.requestConsultantDocumentCreation(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => setFile(undefined)}
                        disabled={!file}
                    >
                        Clear file
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};